import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'
import { Button, Badge, Card, Accordion, Form } from 'react-bootstrap'
import ModalContainer from './ModalContainer'
import Loader from '../../participant_module/components/Loader'
import Message from '../../participant_module/components/Message'
import { getParticipantActivity } from '../actions/jitaiActions'
import { JITAI_PARTICIPANT_ACTIVITY_RESET } from '../constants/jitaiConstants'

const TYPE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'group', label: 'Group assignment' },
  { key: 'guard', label: 'Presentation guard' },
  { key: 'message', label: 'Message / task' },
  { key: 'custom', label: 'Custom response' },
  { key: 'qx', label: 'Questionnaire' },
]

const matchesFilter = (type, filter) => {
  if (filter === 'all') return true
  if (filter === 'group') {
    return (
      type === 'group_assigned' ||
      type === 'group_assignment_evaluated' ||
      type === 'group_assignment_evaluated_true' ||
      type === 'optional_tx_rule_evaluated'
    )
  }
  if (filter === 'guard') {
    return type === 'message_guard_evaluated' || type === 'message_not_presented'
  }
  if (filter === 'message') {
    return (
      type.startsWith('jitai_task_') ||
      type === 'message_presented' ||
      type === 'message_selected' ||
      type === 'message_substitution' ||
      type === 'personalized_message_evaluated'
    )
  }
  if (filter === 'custom') {
    return type.startsWith('custom_resp')
  }
  if (filter === 'qx') {
    return type === 'qx_response_collected'
  }
  return true
}

const badgeVariant = (type) => {
  if (type === 'group_assigned' || type === 'group_assignment_evaluated_true')
    return 'success'
  if (type === 'group_assignment_evaluated') return 'warning'
  if (type === 'message_guard_evaluated') return 'info'
  if (type === 'message_not_presented') return 'danger'
  if (type === 'message_presented' || type === 'message_selected') return 'primary'
  if (type === 'custom_resp_computed' || type === 'custom_resp_eval_log')
    return 'secondary'
  if (type === 'custom_resp_error' || type === 'custom_resp_pending')
    return 'warning'
  if (type === 'qx_response_collected') return 'dark'
  return 'secondary'
}

const LogDisplay = ({ log }) => {
  if (!log || log.length === 0) return null
  return (
    <pre
      style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        padding: '10px',
        fontSize: '0.85rem',
        maxHeight: '260px',
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        marginBottom: 0,
      }}
    >
      {log.map((entry, i) => (
        <div key={i}>{entry}</div>
      ))}
    </pre>
  )
}

export const Last24hActivity = ({ email, studyId, participantId }) => {
  const dispatch = useDispatch()
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const jitaiParticipantActivity = useSelector(
    (state) => state.jitaiParticipantActivity
  )
  const { loading, error, success, activity } = jitaiParticipantActivity

  const [show, setShow] = useState(false)
  const [filter, setFilter] = useState('all')
  const [copyDone, setCopyDone] = useState(false)

  const handleOpen = () => {
    const now = new Date()
    if (userInfo && userInfo.expires > now && !logout) {
      dispatch(getParticipantActivity(studyId, participantId, 24))
    }
    setShow(true)
  }

  const handleClose = () => {
    dispatch({ type: JITAI_PARTICIPANT_ACTIVITY_RESET })
    setFilter('all')
    setCopyDone(false)
    setShow(false)
  }

  const handleRefresh = () => {
    dispatch(getParticipantActivity(studyId, participantId, 24))
    setCopyDone(false)
  }

  const events = (activity && activity.events) || []
  const filtered = events.filter((e) => matchesFilter(e.type, filter))

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(activity || {}, null, 2)
      )
      setCopyDone(true)
    } catch (err) {
      setCopyDone(false)
    }
  }

  return (
    <>
      <Button
        variant='outline-success'
        size='sm'
        className='mr-1 mb-1'
        onClick={handleOpen}
      >
        Last 24h Activity
      </Button>

      <ModalContainer
        show={show}
        handleClose={handleClose}
        title={`Last 24h Activity — ${email}`}
      >
        <div className='mb-3'>
          <p className='text-muted mb-2' style={{ fontSize: '0.9rem' }}>
            Chronological timeline of group assignment, presentation guard,
            message presentation, custom responses, and questionnaire collection
            for this participant. Evaluation logs older than 24 hours are
            auto-deleted.
          </p>
          <div className='d-flex flex-wrap align-items-center mb-2'>
            <Button
              variant='outline-secondary'
              size='sm'
              className='mr-2 mb-1'
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant='outline-secondary'
              size='sm'
              className='mr-2 mb-1'
              onClick={handleCopy}
              disabled={!activity}
            >
              {copyDone ? 'Copied' : 'Copy JSON'}
            </Button>
            <Form.Control
              as='select'
              size='sm'
              className='mr-2 mb-1'
              style={{ maxWidth: 220 }}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              {TYPE_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Form.Control>
            {activity && (
              <span className='text-muted mb-1' style={{ fontSize: '0.85rem' }}>
                {filtered.length} / {activity.eventCount} events · since{' '}
                {moment(activity.since)
                  .tz('Asia/Hong_Kong')
                  .format('YYYY-MM-DD HH:mm')}{' '}
                HKT
              </span>
            )}
          </div>
        </div>

        {loading && <Loader />}
        {error && <Message variant='danger'>{error}</Message>}

        {success && activity && filtered.length === 0 && (
          <Message variant='info'>
            No matching activity in the last 24 hours for this filter.
          </Message>
        )}

        {success &&
          activity &&
          filtered.map((ev, idx) => (
            <Accordion key={`${ev.type}-${ev.at}-${idx}`} className='mb-2'>
              <Card>
                <Card.Header className='py-2'>
                  <Accordion.Toggle
                    as={Button}
                    variant='link'
                    eventKey='0'
                    className='text-left p-0 w-100'
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className='d-flex flex-wrap align-items-center'>
                      <span
                        className='mr-2'
                        style={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          minWidth: 140,
                        }}
                      >
                        {ev.at
                          ? moment(ev.at)
                              .tz('Asia/Hong_Kong')
                              .format('MM-DD HH:mm:ss')
                          : '—'}
                      </span>
                      <Badge variant={badgeVariant(ev.type)} className='mr-2'>
                        {ev.type}
                      </Badge>
                      <span style={{ fontSize: '0.9rem' }}>{ev.summary}</span>
                    </div>
                  </Accordion.Toggle>
                </Card.Header>
                <Accordion.Collapse eventKey='0'>
                  <Card.Body>
                    <div className='mb-2'>
                      <strong>{ev.title}</strong>
                      <div
                        className='text-muted'
                        style={{ fontSize: '0.85rem' }}
                      >
                        source: {ev.source}
                      </div>
                    </div>
                    {ev.details && Object.keys(ev.details).length > 0 && (
                      <div className='mb-2'>
                        <strong>Details</strong>
                        <pre
                          style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            padding: '10px',
                            fontSize: '0.8rem',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {JSON.stringify(ev.details, null, 2)}
                        </pre>
                      </div>
                    )}
                    {ev.log && ev.log.length > 0 && (
                      <div>
                        <strong>Log</strong>
                        <LogDisplay log={ev.log} />
                      </div>
                    )}
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            </Accordion>
          ))}
      </ModalContainer>
    </>
  )
}

export default Last24hActivity
